'use client';

import React from 'react';
import { Button } from '../ui/button';
import { cn } from '~/lib/utils';
import { Star } from './star';

interface ProductDetailsStarRatingProps {
  rating?: number | null;
  ratingCount?: number;
  maxRating?: number;
  className?: string;
  variant?: 'default' | 'compact' | 'list-item';
  hideText?: boolean;
  starSizeOverride?: number;
}

export const ProductDetailsStarRating: React.FC<
  ProductDetailsStarRatingProps
> = ({
  rating,
  ratingCount = 0,
  maxRating = 5,
  className = '',
  variant = 'default',
  hideText,
  starSizeOverride,
}) => {
  // If no rating is provided, return null
  if (rating === undefined || rating === null) {
    return null;
  }

  // Make sure rating is within bounds
  const normalizedRating = Math.max(0, Math.min(rating, maxRating));

  // Create an array of the 5 star positions
  const stars = Array.from({ length: maxRating }, (_, index) => {
    const starValue = index + 1;

    // Determine if this star should be full, half, or empty
    if (normalizedRating >= starValue) {
      return 'full';
    } else if (normalizedRating >= starValue - 0.5) {
      return 'half';
    } else {
      return 'empty';
    }
  });

  const getRatingText = () => {
    switch (variant) {
      case 'list-item':
        return `${rating}/${maxRating}`;
      case 'compact':
        return `${rating} / ${maxRating}`;
      default:
        return `${rating} / ${maxRating}`;
    }
  };

  const getRatingCount = () => {
    return `(${ratingCount})`;
  };

  const ratingText = getRatingText();
  const ratingCountText = getRatingCount();

  const getRatingValue = () => {
    if (hideText) {
      return null;
    }
    if (variant === 'list-item') {
      return ratingText;
    }

    return (
      <Button
        variant="link"
        size="custom"
        onClick={() => {
          const anchor = document.getElementById('product-reviews-trigger');
          if (anchor) {
            if (anchor.dataset.state === 'closed') {
              anchor.click();
            }
            anchor.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            anchor.focus();
          }
        }}
      >
        {ratingText}
      </Button>
    );
  };

  const ratingValue = getRatingValue();

  if (variant === 'list-item') {
    const starSize = starSizeOverride ?? 16;
    return (
      <div
        className={cn(
          `flex items-center justify-center leading-none`,
          className,
        )}
      >
        <span className="text-sm leading-none">{ratingValue}</span>
        <div className="flex items-center">
          <span className="block">
            <Star
              data-star="full"
              width={starSize}
              height={starSize}
              style={{
                fill: 'var(--star-color)',
                stroke: 'var(--star-color)',
              }}
            >
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
            </Star>
          </span>
        </div>
        <span className="text-sm leading-none">{ratingCountText}</span>
      </div>
    );
  }
  if (variant === 'compact') {
    const starSize = starSizeOverride ?? 20;
    return (
      <div className={cn(`flex items-center gap-1`, className)}>
        <span className="text-sm">{ratingValue}</span>
        <div className="flex items-center">
          <span className="block">
            <Star
              data-star="full"
              width={starSize}
              height={starSize}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                fill: 'var(--star-color)',
                stroke: 'var(--star-color)',
              }}
            >
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
            </Star>
          </span>
        </div>
        <span className="text-sm">{ratingCountText}</span>
      </div>
    );
  }

  const starSize = starSizeOverride ?? 24;

  return (
    <div className={cn(`flex items-center gap-2`, className)}>
      <div className="flex items-center">
        {stars.map((type, index) => (
          <span key={index} className="block">
            {type === 'full' && (
              <Star
                data-star="full"
                width={starSize}
                height={starSize}
                style={{
                  fill: 'var(--star-color)',
                  stroke: 'var(--star-color)',
                }}
              />
            )}
            {type === 'half' && (
              <Star
                isHalf
                data-star="half"
                width={starSize}
                height={starSize}
                style={{
                  fill: '#ffffff',
                  stroke: 'var(--star-color)',
                }}
              />
            )}
            {type === 'empty' && (
              <Star
                data-star="empty"
                width={starSize}
                height={starSize}
                style={{
                  fill: '#ffffff',
                  stroke: 'var(--star-color)',
                }}
              />
            )}
          </span>
        ))}
      </div>

      {!hideText && (
        <>
          {ratingValue}
          <span className="text-sm">{ratingCountText}</span>
        </>
      )}
    </div>
  );
};
